import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { warehouseApi } from 'src/api/warehouse';
import { translateKey } from 'src/locales/translate';
import {
  CreateWarehouseSaleDto,
  WarehouseDocumentMutationResponseDto,
} from 'woofwoof-api';
import {
  requireOrganizationId,
  warehouseRequestOptions,
} from '../utils/request';
import { saleErrorMessage, stockWarningProductNames } from '../utils/saleForm';
import { invalidateWarehouseStock } from './useWarehouseProductMutations';

/**
 * A Sale and the organization of the entry point that built it: pinned
 * together, so a retry never goes to another organization (FW-1/4). The
 * operation key travels in `dto`; the entry point owns its series (R2-4).
 */
export interface CreateSaleVariables {
  organizationId: string | undefined;
  dto: CreateWarehouseSaleDto;
}

export interface UseCreateWarehouseSaleOptions {
  onCreated?: (sale: WarehouseDocumentMutationResponseDto) => void;
}

/** "Sprzedaż" (`WZ`): the request names the organization pinned with it (S3). */
export function useCreateWarehouseSale({
  onCreated,
}: UseCreateWarehouseSaleOptions = {}): UseMutationResult<
  WarehouseDocumentMutationResponseDto,
  unknown,
  CreateSaleVariables
> {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ organizationId, dto }: CreateSaleVariables) =>
      (
        await warehouseApi.warehouseSaleControllerCreate(
          { createWarehouseSaleDto: dto },
          warehouseRequestOptions(requireOrganizationId(organizationId)),
        )
      ).data,
    onSuccess: (sale) => {
      if (sale.stockWarnings.length > 0) {
        enqueueSnackbar(
          translateKey('warehouse.sales.createdWithShortage', {
            products: stockWarningProductNames(sale.stockWarnings),
          }),
          { variant: 'warning' },
        );
      } else {
        enqueueSnackbar(translateKey('warehouse.sales.created'), {
          variant: 'success',
        });
      }
      onCreated?.(sale);
    },
    // 409 OPERATION_KEY_REUSED reads as its own message: the key already
    // saved a Sale (a lost answer, then an edited resubmit).
    onError: (error) => {
      enqueueSnackbar(saleErrorMessage(error, translateKey), {
        variant: 'error',
      });
    },
    // An ambiguous answer may hide a committed Sale, and a 409 means one was
    // saved (FW-1/5).
    onSettled: () => {
      invalidateWarehouseStock(queryClient);
      void queryClient.invalidateQueries(['warehouse-documents']);
    },
  });
}
